import { FC, useEffect } from 'react';
import { Link, navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import classNames from 'classnames';
import { ButtonLink, ButtonPrimary, Form, InputText } from '@lambdacurry/component-library';
import { useLoginMutation } from 'lib/home/generated/queries';
import { getQueryParam } from 'lib/util';
import { Card, CardContent, Container, Grid, Layout } from 'lib/components';
import { useLogin } from '../../hooks';
import { useAuthStore } from '../../store';
import { AuthButtons, GettingStartedNoCookingFooter, Logo, InputGrid } from '../../components';
import { default as LoginPattern } from '../../assets/login-pattern.svg';

export const Login: FC = () => {
  const { token } = useAuthStore();
  const login = useLogin();
  const redirectPath = decodeURIComponent(getQueryParam('redirect') || '/');

  useEffect(() => {
    if (token) {
      navigate('/');
    }
  }, []);

  const { mutate } = useLoginMutation<{ response: { data: { login: { token: string } } } }>({
    onSuccess: data => {
      const { token, user } = data.login;
      window.dataLayer.push({ event: 'login', user_id: user.id });
      login(token, { redirectPath });
    }
  });

  return (
    <div className="min-h-screen flex flex-col justify-between">
      <Layout title="Login" className={classNames('login pt-8')}>
        <header className={classNames('h-16 mb-4 py-2 sm:py-12 flex justify-center items-center relative')}>
          <Logo />
        </header>

        <Container>
          <Grid>
            <div className="col-span-12 lg:col-span-10 lg:col-start-2">
              <Card className="relative">
                <div className="md:flex">
                  <CardContent className="my-8 md:my-0 !pb-0">
                    <h2>Login</h2>

                    <AuthButtons redirect="/" />

                    <div className="mt-4 mb-4 text-center">or</div>

                    <Form
                      initialValues={{ email: '', password: '' }}
                      onSubmit={(values, formikHelpers) => {
                        mutate(
                          { loginInput: values },
                          {
                            onError: () => {
                              formikHelpers.resetForm({ values: { ...values, password: '' } });
                            }
                          }
                        );
                      }}
                    >
                      {formikProps => (
                        <>
                          <InputGrid>
                            <InputText label="Email" placeholder="example@email.com" type="email" name="email" />
                            <InputText label="Password" type="password" name="password" />
                          </InputGrid>

                          <div className="mt-6 flex flex-col md:flex-row justify-start gap-4 md:gap-8">
                            <ButtonPrimary className="md:w-56" type="submit" disabled={formikProps.isSubmitting}>
                              Continue
                            </ButtonPrimary>
                            <ButtonLink as={buttonProps => <Link to="/forgot-password" {...buttonProps} />}>
                              Forgot Password?
                            </ButtonLink>
                          </div>
                        </>
                      )}
                    </Form>

                    <div className="border-t mt-8 -mx-4.5 md:-mx-8 lg:-mx-16 p-4.5 md:px-8 lg:px-16">
                      <p className="text-sm text-brand-cool-gray-500 m-0 pr-20">
                        <b>Not a member yet?</b> <Link to="/getting-started">Create&nbsp;an&nbsp;account.</Link>
                      </p>
                    </div>
                  </CardContent>
                  <CardContent className="bg-brand-green-50 min-h-[180px] md:min-h-[500px] md:max-w-md md:min-w-[200px] pr-24 md:pr-0 !border-0" />
                </div>
                <StaticImage
                  className="!absolute bottom-0 right-5 !hidden md:!block h-48 md:h-[500px]"
                  src="../../assets/sage.png"
                  alt="hand holding herbs"
                  placeholder="tracedSVG"
                  height={500}
                  quality={100}
                />
                <StaticImage
                  className="!absolute bottom-0 right-5 md:!hidden"
                  src="../../assets/sage.png"
                  alt="hand holding herbs"
                  placeholder="tracedSVG"
                  height={192}
                />
                <LoginPattern className="max-w-full h-auto bg-brand-green-50" />
              </Card>
            </div>
          </Grid>
        </Container>
      </Layout>
      <GettingStartedNoCookingFooter />
    </div>
  );
};

export default Login;
